import React from 'react';
import { useLocation } from 'react-router-dom';
import { Item } from '../types/Item';
import { Navbar } from '../components';
import jsPDF from 'jspdf';
import '../styles/index.css';

interface LocationState {
  items: Item[];
  totalPrice: number;
  notes: string;
  discount?: number;
  employee: {
    id: string;
    name: string;
    initials: string;
    color: string;
    profilePic?: string;
    bio?: string;
    title?: string;
    address?: string;
    contactLine?: string;
    phone?: string;
    email?: string;
    website?: string;
  };
}

// Helper function for employee-specific localStorage keys
const getStorageKey = (key: string, employeeId: string) => `${key}_${employeeId}`;

type EmployeeInfo = LocationState['employee'];

// Define employees array with contact information (bio is for UI only, not PDF)
const employees: EmployeeInfo[] = [
    { 
      id: '1', 
      name: 'Lizzy Anker', 
      initials: 'LA', 
      color: '#4A90E2', 
      profilePic: '/LizzyAnkerProfilePic.jpg',
      bio: 'Lizzy is Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin non arcu eget velit efficitur convallis et a libero. Integer efficitur suscipit nibh eu imperdiet. Morbi a mauris orci. Sed rhoncus ligula sit amet justo volutpat rutrum. Cras vitae libero rhoncus mauris tempus molestie. Vivamus ex mi, sollicitudin eu congue interdum, lacinia ac risus. Donec pulvinar a metus maximus cursus. Morbi.',
      title: 'Premium Lifestyle Productions v.o.f.',
      address: 'Krekelbos 5, 5521 SV Eersel (NL)',
      contactLine: 'Lizzy Anker',
      phone: '+31 (0)6 - 27016172',
      email: 'lizzy@theartofliving.eu'
    },
    { 
      id: '2', 
      name: 'Lars Geilings', 
      initials: 'LA', 
      color: '#E24A4A', 
      profilePic: '/LarsGeilingsProfilePic.jpeg',
      bio: 'Lars is Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin non arcu eget velit efficitur convallis et a libero. Integer efficitur suscipit nibh eu imperdiet. Morbi a mauris orci. Sed rhoncus ligula sit amet justo volutpat rutrum. Cras vitae libero rhoncus mauris tempus molestie. Vivamus ex mi, sollicitudin eu congue interdum, lacinia ac risus. Donec pulvinar a metus maximus cursus. Morbi.',
      title: 'Premium Lifestyle Productions v.o.f.',
      address: 'Krekelbos 5, 5521 SV Eersel (NL)',
      contactLine: 'Lars Geilings',
      phone: '+31 123 456 78 90',
      email: 'Lars@zijnEmail.com'
    },
    { 
      id: '3', 
      name: 'Rob van Sambeeck', 
      initials: 'RvS', 
      color: '#4AE24A', 
      profilePic: '/RobVanSambeeckProfilePic.png',
      bio: 'Rob is Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin non arcu eget velit efficitur convallis et a libero. Integer efficitur suscipit nibh eu imperdiet. Morbi a mauris orci. Sed rhoncus ligula sit amet justo volutpat rutrum. Cras vitae libero rhoncus mauris tempus molestie. Vivamus ex mi, sollicitudin eu congue interdum, lacinia ac risus. Donec pulvinar a metus maximus cursus. Morbi.',  
      title: 'Premium Lifestyle Productions v.o.f.',
      address: 'Krekelbos 5, 5521 SV Eersel (NL)',
      contactLine: 'Rob Van Sambeeck',
      phone: '(085) 070 94 19',
      email: 'rob@redrockagency.nl'
    }
  ];

const OrderPage: React.FC = () => {
  const location = useLocation();
  const locationState = location.state as LocationState;

  // Load employee first to get the ID for employee-specific data
  const savedEmployee = localStorage.getItem('selectedEmployee');
  
  let employee: LocationState['employee'];
  if (savedEmployee) {
    const parsedEmployee = JSON.parse(savedEmployee);
    // Always get the full employee data from employees to ensure we have all contact info
    const fullEmployee = employees.find(emp => emp.id === parsedEmployee.id);
    if (fullEmployee) {
      // Update localStorage with complete employee data
      localStorage.setItem('selectedEmployee', JSON.stringify(fullEmployee));
      employee = fullEmployee;
    } else {
      employee = parsedEmployee;
    }
  } else {
    employee = locationState?.employee || employees[0];
  }

  // Load employee-specific data from localStorage if available, otherwise use location state
  const employeeId = employee?.id || '';
  const savedItems = employeeId ? localStorage.getItem(getStorageKey('canvasItems', employeeId)) : null;
  const savedNotes = employeeId ? localStorage.getItem(getStorageKey('notes', employeeId)) : null;
  const savedDiscount = employeeId ? localStorage.getItem(getStorageKey('discount', employeeId)) : null;
  const savedShowDiscount = employeeId ? localStorage.getItem(getStorageKey('showDiscount', employeeId)) : null;
  const savedCustomerInfo = employeeId ? localStorage.getItem(getStorageKey('customerInfo', employeeId)) : null;
  
  const items = savedItems ? JSON.parse(savedItems) : (locationState?.items || []);
  const notes = savedNotes || (locationState?.notes || '');
  const showDiscount = savedShowDiscount ? JSON.parse(savedShowDiscount) : false;
  const customerInfo = savedCustomerInfo ? JSON.parse(savedCustomerInfo) : {
    companyName: '',
    email: '',
    phone: '',
    contactPerson: ''
  };

  const discount = savedDiscount ? parseFloat(savedDiscount) : (locationState?.discount || 0);
  
  const subtotal = items.reduce((sum: number, item: Item) => sum + item.price, 0);
  const discountAmount = subtotal * (discount / 100);
  const totalPrice = subtotal - discountAmount;

  // Define interface for grouped items
  interface GroupedItem {
    name: string;
    price: number;
    count: number;
  }

  // Group items by name and count them
  const groupedItems = items.reduce((acc: GroupedItem[], item: Item) => {
    const existing = acc.find((group: GroupedItem) => group.name === item.name && group.price === item.price);
    if (existing) {
      existing.count += 1;
    } else {
      acc.push({ name: item.name, price: item.price, count: 1 });
    }
    return acc;
  }, [] as Array<{ name: string; price: number; count: number }>);

  const generatePDF = async () => {
    // Always get the full employee data from employees array to ensure we have all contact info
    const fullEmployee = employees.find(emp => emp.id === employee.id) || employee;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Color palette (RGB values) - matching dashboard colors
    const colors = {
      headerBrown: [33, 22, 19] as [number, number, number],      // #211613 - Dark brown header
      gold: [162, 144, 97] as [number, number, number],             // #A29061 - Gold accent
      lightBeige: [232, 229, 227] as [number, number, number],     // #E8E5E3 - Light beige background
      darkBeige: [209, 203, 199] as [number, number, number],      // #D1CBC7 - Beige background
      text: [33, 22, 19] as [number, number, number],              // #211613 - Dark text
      textSecondary: [92, 83, 76] as [number, number, number],    // #5c534c - Secondary text
      border: [169, 151, 131] as [number, number, number],         // #a99783 - Border color
      lightGrey: [128, 128, 128] as [number, number, number]        // Light grey for footer
    };
    
    // Helper function to check if content fits on current page and add new page if needed
    const checkPageBreak = (requiredHeight: number, margin: number = 20): void => {
      // Calculate if we need a new page (leave margin at bottom for footer)
      if (yPosition + requiredHeight > pageHeight - margin) {
        // Add new page
        doc.addPage();
        // Set background for new page
        doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        // Reset yPosition for new page
        yPosition = 10;
      }
    };
    
    // Set page background to light beige (dashboard background color)
    doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    
    let yPosition = 10; // Start higher up on the page
    
    // Load logo image as base64 with dimensions
    const loadLogo = (): Promise<{ dataUrl: string; width: number; height: number }> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL('image/png');
            resolve({ dataUrl, width: img.width, height: img.height });
          } else {
            reject(new Error('Could not get canvas context'));
          }
        };
        img.onerror = () => {
          reject(new Error('Failed to load logo image'));
        };
        img.src = '/Logo.png';
      });
    };
    
    // Logo at top left - like standard invoices
    try {
      // Load logo and calculate dimensions preserving aspect ratio
      const logoData = await loadLogo();
      const aspectRatio = logoData.width / logoData.height;
      
      // Logo size - appropriate for invoice header
      const maxLogoHeight = 18; // Logo height
      const logoHeight = maxLogoHeight;
      const logoWidth = logoHeight * aspectRatio;
      
      // Make sure logo doesn't exceed reasonable width
      const maxLogoWidth = pageWidth * 0.35; // Max 35% of page width
      let finalLogoWidth = logoWidth;
      let finalLogoHeight = logoHeight;
      
      if (logoWidth > maxLogoWidth) {
        finalLogoWidth = maxLogoWidth;
        finalLogoHeight = maxLogoWidth / aspectRatio;
      }
      
      // Position logo centered at top
      const logoX = (pageWidth - finalLogoWidth) / 2; // Centered
      const logoY = yPosition;
      
      doc.addImage(logoData.dataUrl, 'PNG', logoX, logoY, finalLogoWidth, finalLogoHeight);
      yPosition += finalLogoHeight + 2; // Very minimal space after logo
    } catch (error) {
      // Fallback to text if logo fails to load
      console.warn('Logo failed to load, using text fallback:', error);
      doc.setTextColor(colors.gold[0], colors.gold[1], colors.gold[2]);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('the art of living®', 20, yPosition + 8, { align: 'left' });
      yPosition += 20;
    }
    
    // Customer Info Section - "Ter attentie van" (only if customer info exists)
    if (customerInfo && (customerInfo.companyName || customerInfo.contactPerson || customerInfo.email)) {
      const customerInfoLines: { label: string; value: string }[] = [];
      if (customerInfo.companyName) {
        customerInfoLines.push({ label: 'Bedrijfsnaam:', value: customerInfo.companyName });
      }
      if (customerInfo.contactPerson) {
        customerInfoLines.push({ label: 'Contactpersoon:', value: customerInfo.contactPerson });
      }
      if (customerInfo.email) {
        customerInfoLines.push({ label: 'Email:', value: customerInfo.email });
      }
      
      if (customerInfoLines.length > 0) {
        const customerPadding = 4;
        const customerBoxHeight = customerPadding * 2 + (customerInfoLines.length * 5) + 2;
        const customerBoxY = yPosition;
        
        // Draw customer info box with beige background
        doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
        doc.rect(20, customerBoxY, pageWidth - 40, customerBoxHeight, 'F');
        
        // "Ter attentie van" label - bold, aligned left like "Totale overzicht"
        doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Ter attentie van:', 20, customerBoxY + customerPadding + 3);
        
        // Customer info lines with labels
        let customerY = customerBoxY + customerPadding + 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        customerInfoLines.forEach((line) => {
          doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
          doc.text(`${line.label}`, 20, customerY);
          // Value in slightly different position for better alignment
          const labelWidth = doc.getTextWidth(`${line.label} `);
          doc.text(line.value, 20 + labelWidth, customerY);
          customerY += 5;
        });
        
        yPosition += customerBoxHeight + 12; // More space before "Totale overzicht"
      }
    }
    
    // Section: "Totale overzicht:" - modern, softer styling
    doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Totale overzicht', 20, yPosition);
    
    yPosition += 1; // Minimal space between title and items box
    
    // Large box for items list - beige background with minimal padding
    const rowHeight = 7; // Very compact spacing between rows
    const padding = 4; // Minimal padding
    const headerHeight = 6; // Space for column headers
    
    // Calculate available space on current page
    const availableHeight = pageHeight - yPosition - 20; // Leave margin for footer
    const maxRowsPerPage = Math.floor((availableHeight - padding * 2 - headerHeight) / rowHeight);
    
    // Split items across pages if needed
    let itemsStartIndex = 0;
    let isFirstPage = true;
    
    while (itemsStartIndex < groupedItems.length) {
      // Check if we need a new page (except for first page)
      if (!isFirstPage) {
        doc.addPage();
        doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        yPosition = 10;
        
        // Add column headers on new page
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.textSecondary[0], colors.textSecondary[1], colors.textSecondary[2]);
        const headerY = yPosition + padding + 2;
        doc.text('Item', 20, headerY);
        doc.text('Aantal', 102, headerY);
        doc.text('Prijs/stuk', 135, headerY);
        doc.text('Totaal', pageWidth - 20, headerY, { align: 'right' });
        
        // Draw subtle line under headers
        doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
        doc.setLineWidth(0.1);
        doc.line(20, headerY + 2, pageWidth - 20, headerY + 2);
        
        yPosition = headerY + 2 + 4; // Start items after header
      }
      
      // Calculate how many items fit on this page
      const itemsEndIndex = Math.min(itemsStartIndex + maxRowsPerPage, groupedItems.length);
      const itemsOnThisPage = groupedItems.slice(itemsStartIndex, itemsEndIndex);
      const itemsBoxHeight = (itemsOnThisPage.length * rowHeight) + (padding * 2) + (isFirstPage ? headerHeight : 0);
      const itemsBoxY = isFirstPage ? yPosition : yPosition - padding - 2;
      
      // Draw main box with beige background - softer, less blocky
      doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
      doc.rect(20, itemsBoxY, pageWidth - 40, itemsBoxHeight, 'F');
      
      // Add column headers only on first page
      if (isFirstPage) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.textSecondary[0], colors.textSecondary[1], colors.textSecondary[2]);
        const headerY = itemsBoxY + padding + 2;
        doc.text('Item', 20, headerY);
        doc.text('Aantal', 102, headerY); // Slightly more to the right
        doc.text('Prijs/stuk', 135, headerY);
        doc.text('Totaal', pageWidth - 20, headerY, { align: 'right' });
        
        // Draw subtle line under headers
        doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
        doc.setLineWidth(0.1);
        doc.line(20, headerY + 2, pageWidth - 20, headerY + 2);
      }
      
      // Add items to the box with better spacing
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      let itemY = itemsBoxY + padding + (isFirstPage ? 8 : 2); // More space after header on first page
      
      for (let i = 0; i < itemsOnThisPage.length; i++) {
        const item = itemsOnThisPage[i];
        // Check if item fits on current page
        if (itemY + rowHeight > pageHeight - 20) {
          // This shouldn't happen due to our calculation, but as safety check
          break;
        }
        
        doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
        doc.text(item.name, 20, itemY);
        // Use secondary color for count and price
        doc.setTextColor(colors.textSecondary[0], colors.textSecondary[1], colors.textSecondary[2]);
        doc.text(item.count.toString(), 102, itemY); // Slightly more to the right to match header
        doc.text(item.price === 0 ? '€ op maat' : `€${item.price.toFixed(2)}`, 135, itemY);
        // Use gold color for total price
        doc.setTextColor(colors.gold[0], colors.gold[1], colors.gold[2]);
        doc.setFont('helvetica', 'bold');
        // For total, show "€ op maat" if price is 0, otherwise show calculated total
        const itemTotal = item.price * item.count;
        doc.text(item.price === 0 ? '€ op maat' : `€${itemTotal.toFixed(2)}`, pageWidth - 20, itemY, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
        itemY += rowHeight;
      }
      
      yPosition = itemY + padding;
      itemsStartIndex = itemsEndIndex;
      isFirstPage = false;
    }
    
    // Space between items and summary - less space if there are few items
    const itemCount = groupedItems.length;
    const spaceBeforeSummary = itemCount <= 5 ? -5 : 0; // Very close to "Totale overzicht" if few items
    
    // Adjust yPosition based on item count
    if (spaceBeforeSummary !== 0) {
      yPosition += spaceBeforeSummary;
    }
    
    // Summary box - minimal padding
    const summaryPadding = 4; // Minimal padding
    const lineSpacing = 6; // 6px spacing between each of the three lines
    const totalBarHeight = 8; // Very compact height
    
    // Calculate actual summary height before drawing
    let whiteSectionHeight = 0;
    if (showDiscount && discount > 0) {
      // Height for discount section: padding + 2 lines with spacing
      whiteSectionHeight = summaryPadding * 2 + 2 + lineSpacing + 4; // Approximate height for 2 lines
    }
    // Total height: white section (if exists) + total bar
    const actualSummaryHeight = whiteSectionHeight + totalBarHeight + 4; // Add extra margin
    
    // Check if summary fits on current page with proper margin
    checkPageBreak(actualSummaryHeight, 30); // Increased margin to ensure nothing gets cut off
    
    const summaryBoxY = yPosition;
    let summaryBoxHeight = 0;
    
    // Draw summary box white background section (only if discount)
    if (showDiscount && discount > 0) {
      // Calculate positions with 6px spacing between each line
      const firstLineY = summaryBoxY + summaryPadding + 2;
      const secondLineY = firstLineY + lineSpacing;
      
      whiteSectionHeight = (secondLineY - summaryBoxY) + summaryPadding + 2;
      
      doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
      doc.rect(20, summaryBoxY, pageWidth - 40, whiteSectionHeight, 'F');
      
      // Draw text with 6px spacing
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
      
      const rightMargin = pageWidth - 20;
      
      // Subtotaal
      doc.text(`Subtotaal (${items.length} items):`, 20, firstLineY);
      doc.text(`€${subtotal.toFixed(2)}`, rightMargin, firstLineY, { align: 'right' });
      
      // Korting in gold
      doc.setTextColor(colors.gold[0], colors.gold[1], colors.gold[2]);
      doc.setFont('helvetica', 'bold');
      doc.text(`Korting (${discount}%):`, 20, secondLineY);
      doc.text(`-€${discountAmount.toFixed(2)}`, rightMargin, secondLineY, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    }
    
    // Totaal bedrag - beige background with gold text, very compact
    // Position it so spacing between all three lines is 6px
    const totalBarY = summaryBoxY + whiteSectionHeight;
    
    // Draw beige background
    doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
    doc.rect(20, totalBarY, pageWidth - 40, totalBarHeight, 'F');
    
    // Total text - gold color, bold, with 6px spacing from "Korting"
    doc.setTextColor(colors.gold[0], colors.gold[1], colors.gold[2]);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    // Calculate Y position: if discount exists, use secondLineY + 6px, otherwise use firstLineY + 6px
    const kortingY = showDiscount && discount > 0 ? summaryBoxY + summaryPadding + 2 + lineSpacing : summaryBoxY + summaryPadding + 2;
    const totalTextY = kortingY + lineSpacing; // Exactly 6px spacing
    
    // Ensure totalTextY fits on page (with margin for text height)
    if (totalTextY + 5 > pageHeight - 20) {
      // Need new page for total
      doc.addPage();
      doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      yPosition = 10;
      
      // Redraw total bar on new page
      const newTotalBarY = yPosition;
      doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
      doc.rect(20, newTotalBarY, pageWidth - 40, totalBarHeight, 'F');
      
      doc.setTextColor(colors.gold[0], colors.gold[1], colors.gold[2]);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Totaal bedrag:', 20, newTotalBarY + 5);
      doc.text(`€${totalPrice.toFixed(2)}`, pageWidth - 20, newTotalBarY + 5, { align: 'right' });
      
      summaryBoxHeight = totalBarHeight;
      yPosition = newTotalBarY + totalBarHeight;
    } else {
      doc.text('Totaal bedrag:', 20, totalTextY);
      doc.text(`€${totalPrice.toFixed(2)}`, pageWidth - 20, totalTextY, { align: 'right' });
      
      // Calculate total summary box height
      summaryBoxHeight = whiteSectionHeight + totalBarHeight;
    }
    
    // No border - creates softer, more rounded appearance (like dashboard with border-radius)
    
    // More space between summary and notes if there's no discount, otherwise keep minimal space
    const spaceAfterSummary = showDiscount && discount > 0 ? 4 : 12; // More space if no discount
    yPosition += summaryBoxHeight + spaceAfterSummary;
    
    // Notes section - modern, less blocky styling
    if (notes && notes.trim()) {
      const maxWidth = pageWidth - 60;
      const notesLines = doc.splitTextToSize(notes, maxWidth);
      const notesPadding = 4; // Minimal padding
      const notesBoxHeight = Math.max(20, notesLines.length * 4 + notesPadding * 2 + 4); // Very compact
      
      // Check if notes fit on current page
      checkPageBreak(notesBoxHeight);
      
      // Notes box with beige background - more padding
      doc.setFillColor(colors.lightBeige[0], colors.lightBeige[1], colors.lightBeige[2]);
      doc.rect(20, yPosition, pageWidth - 40, notesBoxHeight, 'F');
      
      // No border - creates softer, more rounded appearance (like dashboard with border-radius)
      
      // "Notities" title - bold, aligned left like "Totale overzicht"
      doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Notities', 20, yPosition + notesPadding + 2);
      
      // Notes text with spacing from title
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
      doc.text(notesLines, 20, yPosition + notesPadding + 10);
      
      yPosition += notesBoxHeight + 12; // More space between notes and contact info
    }
    
    // Contact Information Section - beige background, minimal padding
    const fallbackCompany = 'Premium Lifestyle Productions v.o.f.';
    const contactHeading = 'Vragen? Neem contact op:';
    const contactLines: string[] = [];

    const companyLine = fullEmployee.title || fallbackCompany;
    if (companyLine) {
      contactLines.push(companyLine);
    }
    if (fullEmployee.address) {
      contactLines.push(fullEmployee.address);
    }

    const hasPersonDetails = fullEmployee.contactLine || fullEmployee.phone || fullEmployee.email || fullEmployee.website;
    if (hasPersonDetails) {
      contactLines.push('');
      contactLines.push(contactHeading);
    }

    if (fullEmployee.contactLine) {
      contactLines.push(fullEmployee.contactLine);
    }
    if (fullEmployee.phone) {
      contactLines.push(`M ${fullEmployee.phone}`);
    }
    if (fullEmployee.email) {
      contactLines.push(`E ${fullEmployee.email}`);
    }
    if (fullEmployee.website) {
      contactLines.push(`W ${fullEmployee.website}`);
    }

    const contactLinesForFooter = [...contactLines];
    const footerHasHeading = contactLinesForFooter.includes(contactHeading);
    
    // Footer - centered, small, light grey (on all pages)
    const totalPages = Math.max(1, doc.internal.pages.length - 1); // jsPDF uses 1-based indexing but array is 0-based
    const footerText = `Gegenereerd op ${new Date().toLocaleDateString('nl-NL', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    })}, ${new Date().toLocaleTimeString('nl-NL', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    })}`;
    
    // Add footer only on the last page
    doc.setPage(totalPages);

    if (contactLinesForFooter.length > 0) {
      const footerLineHeight = 5;
      const footerBlockHeight = contactLinesForFooter.length * footerLineHeight;
      let footerY = pageHeight - (footerBlockHeight + 20);

      doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
      doc.setFontSize(9);

      contactLinesForFooter.forEach((line) => {
        if (line.trim() === '') {
          footerY += footerLineHeight;
          return;
        }

        const isHeading = footerHasHeading && line === contactHeading;
        const isContactName = !!fullEmployee.contactLine && line === fullEmployee.contactLine;
        doc.setFont('helvetica', isHeading || isContactName ? 'bold' : 'normal');
        doc.text(line, 20, footerY);
        footerY += footerLineHeight;
      });
    }

    doc.setTextColor(colors.lightGrey[0], colors.lightGrey[1], colors.lightGrey[2]);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(footerText, pageWidth / 2, pageHeight - 8, { align: 'center' });
    
    // Download the PDF
    // Generate filename: TheArtOfLiving-Pakket-[bedrijfsnaam of datum]
    let fileName = 'TheArtOfLiving-Pakket-';
    
    if (customerInfo && customerInfo.companyName && customerInfo.companyName.trim()) {
      // Use company name (sanitize for filename: remove special chars, replace spaces with dashes)
      const sanitizedCompanyName = customerInfo.companyName
        .trim()
        .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters except spaces and dashes
        .replace(/\s+/g, '-') // Replace spaces with dashes
        .toLowerCase();
      fileName += sanitizedCompanyName;
    } else {
      // Use date if no company name
      const date = new Date().toISOString().split('T')[0];
      fileName += date;
    }
    
    fileName += '.pdf';
    doc.save(fileName);
  };

  return (
    <div className="app">
      <Navbar 
        selectedEmployee={employee}
        onEmployeeChange={() => {}}
        employees={[employee]}
        hideEmployeeSelector={true}
      />
      <div className="order-page">
        <div className="order-container">
          <div className="order-header">
            <h1>Pakket Overzicht</h1>
          </div>

          <div className="order-content">
            <div className="order-grid">
              <div className="left-column-container">
                <div className="items-section">
                  <h2>Geselecteerde Items</h2>
                  <div className="items-list">
                    {groupedItems.map((item: GroupedItem, index: number) => (
                      <div key={`${item.name}-${item.price}-${index}`} className="summary-item">
                        <div className="item-details">
                          <div className="item-name">
                            {item.name}
                            {item.count > 1 && (
                              <span className="item-quantity"> {item.count} stuks</span>
                            )}
                          </div>
                        </div>
                        <div className="item-total">
                          {item.price === 0 ? '€ op maat' : `€${(item.price * item.count).toFixed(2)}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="total-section">
                  <div className="total-breakdown">
                    <div className="total-line">
                      <span>Aantal items:</span>
                      <span>{items.length}</span>
                    </div>
                    
                    {showDiscount && discount > 0 && (
                      <>
                        <div className="total-line">
                          <span>Subtotaal:</span>
                          <span>€{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="total-line discount-line">
                          <span>Korting ({discount}%):</span>
                          <span>-€{discountAmount.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    
                    <div className="total-line main-total">
                      <span>Totaal bedrag:</span>
                      <span className="total-amount">€{totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {employee.bio && (
                <div className="manager-section">
                  <h2>Uw Contactpersoon</h2>
                  <div className="manager-info">
                    <div className="manager-header">
                      <div className="manager-avatar">
                        {employee.profilePic ? (
                          <img 
                            src={employee.profilePic} 
                            alt={employee.name}
                            className="manager-profile-image"
                          />
                        ) : (
                          <span className="manager-initials" style={{ backgroundColor: employee.color }}>
                            {employee.initials}
                          </span>
                        )}
                      </div>
                      <h3>{employee.name}</h3>
                    </div>
                    <p className="manager-bio">{employee.bio}</p>
                  </div>
                  <div className="notes-section">
                    <h2>Notities</h2>
                    <div className="notes-content">
                      {notes || 'Geen notities toegevoegd'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="action-buttons">
              <button 
                className="back-button"
                onClick={() => window.history.back()}
              >
                Terug naar Dashboard
              </button>
              <button 
                className="download-pdf-button"
                onClick={generatePDF}
              >
                📄 Download als PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderPage;
